import React from "react";
import { Globe, Swords, Users, Bot, UserCheck, Ticket, Calendar, Flame, Coins, ShieldAlert, FileText } from "lucide-react";
import useFetch from "@/lib/useFetch";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatCard from "@/components/common/StatCard";
import DiscordHealthCard from "@/components/DiscordHealthCard";
import { timeAgo } from "@/lib/communities";

export default function Overview() {
    const { data } = useFetch("/admin/overview");
    if (!data) return <Loader />;

    const cards = [
        { icon: Globe, label: "Communities", value: data.communities, accent: "#FF6B00" },
        { icon: Swords, label: "Players Online", value: data.players_online, accent: "#00FF66" },
        { icon: Users, label: "Registered Members", value: data.registered_members, accent: "#F59E0B" },
        { icon: Bot, label: "Discord Members", value: (data.discord_members || 0).toLocaleString(), accent: "#5865F2" },
        { icon: UserCheck, label: "Discord Connected", value: data.discord_connected_users, accent: "#00FF66" },
        { icon: Ticket, label: "Open Tickets", value: data.open_tickets, accent: "#EF4444" },
        { icon: Calendar, label: "Active Events", value: data.active_events, accent: "#FF6B00" },
        { icon: Flame, label: "Upcoming Purges", value: data.upcoming_purges, accent: "#D9480F" },
        { icon: Coins, label: "Economy Txns", value: data.economy_transactions, accent: "#F59E0B" },
    ];

    return (
        <div data-testid="admin-overview">
            <PageHeader title="Command Overview" subtitle="Network-wide operational status." />
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
                {cards.map((c) => <StatCard key={c.label} {...c} testId={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`} />)}
            </div>

            <div className="mb-6"><DiscordHealthCard status={data.discord_status} /></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <SectionCard title="Recent Audit Actions" testId="overview-audit">
                    <div className="space-y-2">
                        {(data.recent_audit || []).length === 0 && <div className="text-sm text-muted-foreground">No actions logged yet.</div>}
                        {(data.recent_audit || []).map((a) => (
                            <div key={a.id} className="flex items-center gap-3 rounded-md border border-border bg-background/40 p-2.5">
                                <FileText size={15} className="text-hazard" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-foreground truncate">{a.action} {a.target && <span className="text-muted-foreground">— {a.target}</span>}</div>
                                    <div className="label-mono text-muted-foreground">{a.administrator} · {timeAgo(a.created_at)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </SectionCard>
                <SectionCard title="Recent Moderation" testId="overview-moderation">
                    <div className="space-y-2">
                        {(data.recent_moderation || []).length === 0 && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <ShieldAlert size={15} className="text-radioactive" /> No recent moderation actions. All quiet.
                            </div>
                        )}
                        {(data.recent_moderation || []).map((m) => (
                            <div key={m.id} className="text-sm text-foreground">{m.action} — {m.player}</div>
                        ))}
                    </div>
                </SectionCard>
            </div>
            <Footer />
        </div>
    );
}
